import { useEffect, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import axios from "axios";
import { toast } from "react-toastify";
import SelectSearchInput from "../SelectSearch";
import Select from "react-select";
import { FaEdit, FaTrashAlt } from "react-icons/fa";

const ReceivedInventory = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [stockGroups, setStockGroups] = useState([]);
    const [token, setToken] = useState(localStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [quantity, setQuantity] = useState("");
    const [rate, setRate] = useState("");
    const [receivedItems, setReceivedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingItemId, setEditingItemId] = useState(null);

    useEffect(() => {
        const fetchStockGroups = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.post(`${baseUrl}/getstocksbygroup`, {
                    token,
                    outlet_name: selectedOutlet
                });
                setStockGroups(response.data);
            } catch (error) {
                console.error('Error fetching stock groups:', error);
                toast.error('Error fetching stock items');
            }
        };

        fetchStockGroups();
    }, [token, selectedOutlet, baseUrl]);

    const handleAddItem = () => {
        if (!selectedItem) {
            toast.error("Please select an item");
            return;
        }
        if (!quantity || isNaN(quantity) || parseFloat(quantity) <= 0) {
            toast.error("Please enter a valid quantity");
            return;
        }
        if (!rate || isNaN(rate) || parseFloat(rate) <= 0) {
            toast.error("Please enter a valid rate");
            return;
        }

        const existingItemIndex = receivedItems.findIndex(item => item.id === selectedItem.id);

        if (existingItemIndex >= 0) {
            const updatedItems = [...receivedItems];
            updatedItems[existingItemIndex].quantity = parseFloat(quantity);
            updatedItems[existingItemIndex].price = parseFloat(rate);
            setReceivedItems(updatedItems);
        } else {
            setReceivedItems([...receivedItems, {
                id: selectedItem.id,
                item_name: selectedItem.name,
                quantity: parseFloat(quantity),
                price: parseFloat(rate),
                uom: selectedItem.uom
            }]);
        }

        setSelectedItem(null);
        setQuantity("");
        setRate("");
        setEditingItemId(null);
    };

    const handleEditItem = (item) => {
        setSelectedItem({
            id: item.id,
            name: item.item_name,
            uom: item.uom
        });
        setQuantity(item.quantity);
        setRate(item.price);
        setEditingItemId(item.id);
    };

    const handleCancelEdit = () => {
        setSelectedItem(null);
        setQuantity("");
        setRate("");
        setEditingItemId(null);
    };

    const handleRemoveItem = (id) => {
        setReceivedItems(receivedItems.filter(item => item.id !== id));
        if (editingItemId === id) {
            handleCancelEdit();
        }
    };

    const handleSubmit = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save these received items?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!token || !selectedOutlet) {
                                toast.error("Please select an outlet");
                                return;
                            }
                            if (receivedItems.length === 0) {
                                toast.error("Please add at least one item");
                                return;
                            }

                            setIsLoading(true);
                            try {
                                const today = new Date().toLocaleDateString('en-CA')
                                const payload = {
                                    token,
                                    outlet_name: selectedOutlet,
                                    received_date: today,
                                    items: receivedItems.map(item => ({
                                        item_name: item.item_name,
                                        quantity: item.quantity,
                                        price: item.price,
                                        uom: item.uom
                                    }))
                                };

                                await axios.post(`${baseUrl}/received-items`, payload);
                                toast.success("Inventory received successfully!");
                                setReceivedItems([]);
                                setEditingItemId(null);
                            } catch (error) {
                                console.error('Error submitting received inventory:', error);
                                toast.error('Error submitting received inventory');
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-received-inventory'
            }
        );
    };

    const itemOptions = stockGroups.flatMap(group =>
        group.stocks.map(stock => ({
            value: stock.ItemName,
            label: stock.ItemName,
            data: {
                id: stock.ItemName,
                name: stock.ItemName,
                uom: stock.UOM
            }
        }))
    );

    const handleQuantityChange = (id, newQuantity) => {
        if (isNaN(newQuantity) || parseFloat(newQuantity) <= 0) return;

        setReceivedItems(receivedItems.map(item =>
            item.id === id ? { ...item, quantity: parseFloat(newQuantity) } : item
        ));
    };

    const handleRateChange = (id, newRate) => {
        if (isNaN(newRate) || parseFloat(newRate) <= 0) return;

        setReceivedItems(receivedItems.map(item =>
            item.id === id ? { ...item, price: parseFloat(newRate) } : item
        ));
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-5xl my-5 mx-auto">
                    <div className="flex justify-center text-center mb-4">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    {token && selectedOutlet && (
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-semibold mb-4">Receive Inventory</h2>

                            <div className="grid grid-cols-3 gap-4 mb-6">
                                <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                                    <Select
                                        options={itemOptions}
                                        value={selectedItem ? {
                                            value: selectedItem.id,
                                            label: selectedItem.name,
                                            data: selectedItem
                                        } : null}
                                        onChange={(selectedOption) => {
                                            setSelectedItem(selectedOption?.data || null);
                                        }}
                                        isSearchable
                                        placeholder="Search or select item..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                <div className="col-span-2 grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Rate</label>
                                        <input
                                            type="number"
                                            className="w-full p-2 border border-gray-300 rounded-md"
                                            value={rate}
                                            onChange={(e) => setRate(e.target.value)}
                                            min="0"
                                            step="any"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">UOM</label>
                                        <input
                                            type="text"
                                            className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                                            value={selectedItem?.uom || ""}
                                            readOnly
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                                        <input
                                            type="number"
                                            className="w-full p-2 border border-gray-300 rounded-md"
                                            value={quantity}
                                            onChange={(e) => setQuantity(e.target.value)}
                                            min="0"
                                            step="any"
                                        />
                                    </div>
                                </div>

                                <div className="col-span-2 grid grid-cols-3 gap-4">
                                    <div className="flex items-end gap-2">
                                        <button
                                            className={`w-full py-2 px-4 rounded-md ${editingItemId ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                                            onClick={handleAddItem}
                                        >
                                            {editingItemId ? "Update Item" : "Add Item"}
                                        </button>
                                        {editingItemId && (
                                            <button
                                                className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700"
                                                onClick={handleCancelEdit}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {receivedItems.length > 0 && (
                                <div className="mt-6">
                                    <h3 className="text-lg font-medium mb-2">Items to Receive</h3>
                                    <div className="rounded-md border overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">UOM</th>
                                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {receivedItems.map((item) => (
                                                    <tr key={item.id} className={editingItemId === item.id ? "bg-yellow-50" : ""}>
                                                        <td className="px-6 py-4 whitespace-nowrap">{item.item_name}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <input
                                                                type="number"
                                                                className="w-20 text-center p-1 border border-gray-300 rounded-md"
                                                                value={item.price}
                                                                onChange={(e) => handleRateChange(item.id, e.target.value)}
                                                                min="0"
                                                                step="any"
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <input
                                                                type="number"
                                                                className="w-20 text-center p-1 border border-gray-300 rounded-md"
                                                                value={item.quantity}
                                                                onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                                min="0"
                                                                step="any"
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">{item.uom}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap">{item.price * item.quantity}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap space-x-2">
                                                            <button
                                                                className="text-blue-600 hover:text-blue-900"
                                                                onClick={() => handleEditItem(item)}
                                                            >
                                                                <FaEdit className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                className="text-red-600 hover:text-red-900"
                                                                onClick={() => handleRemoveItem(item.id)}
                                                            >
                                                                <FaTrashAlt className="h-4 w-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-4 flex justify-end">
                                        <button
                                            className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                                            onClick={handleSubmit}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? "Saving..." : "Save"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ReceivedInventory;